import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type ThemeMode = 'dark' | 'light';

export interface ThemeCfg {
  mode: ThemeMode;
  primaryColor: string;
  accentColor: string;
  sidebarColor: string;
  logoText: string;
  orgNameOverride: string;
  logoUrl: string;
}

const DEFAULT: ThemeCfg = {
  mode: 'dark',
  primaryColor: '#3b82f6',
  accentColor: '#1d4ed8',
  sidebarColor: '#0d0d0d',
  logoText: 'L',
  orgNameOverride: '',
  logoUrl: '',
};

// ─── CSS variables injected per mode ─────────────────────────────────────────

const DARK_VARS = {
  '--lm-bg':          '#0a0f1e',
  '--lm-bg2':         '#0d0d0d',
  '--lm-surface':     'rgba(255,255,255,0.03)',
  '--lm-surface2':    'rgba(255,255,255,0.06)',
  '--lm-border':      'rgba(255,255,255,0.06)',
  '--lm-border2':     'rgba(255,255,255,0.1)',
  '--lm-text':        '#e2e8f0',
  '--lm-text2':       '#94a3b8',
  '--lm-text3':       '#555555',
  '--lm-text4':       '#555555',
};

const LIGHT_VARS = {
  '--lm-bg':          '#f1f5f9',
  '--lm-bg2':         '#e2e8f0',
  '--lm-surface':     'rgba(255,255,255,0.92)',
  '--lm-surface2':    'rgba(0,0,0,0.04)',
  '--lm-border':      'rgba(0,0,0,0.08)',
  '--lm-border2':     'rgba(0,0,0,0.14)',
  '--lm-text':        '#0a0a0a',
  '--lm-text2':       '#111111',
  '--lm-text3':       '#374151',
  '--lm-text4':       '#4b5563',
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface ThemeCtxValue {
  theme: ThemeCfg;
  updateTheme: (partial: Partial<ThemeCfg>) => void;
  resetTheme: () => void;
  toggleMode: () => void;
  isDark: boolean;
}

const ThemeCtx = createContext<ThemeCtxValue>({
  theme: DEFAULT,
  updateTheme: () => {},
  resetTheme: () => {},
  toggleMode: () => {},
  isDark: true,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ThemeCfg>(() => {
    try {
      const s = localStorage.getItem('lemai_theme');
      return s ? { ...DEFAULT, ...JSON.parse(s) } : DEFAULT;
    } catch { return DEFAULT; }
  });

  useEffect(() => {
    localStorage.setItem('lemai_theme', JSON.stringify(theme));
    const root = document.documentElement;
    // Branding vars
    root.style.setProperty('--lm-primary', theme.primaryColor);
    root.style.setProperty('--lm-accent', theme.accentColor);
    root.style.setProperty('--lm-sidebar', theme.sidebarColor);
    // Mode vars
    const modeVars = theme.mode === 'dark' ? DARK_VARS : LIGHT_VARS;
    Object.entries(modeVars).forEach(([k, v]) => root.style.setProperty(k, v));
    // data-theme attribute for CSS overrides
    document.documentElement.setAttribute('data-theme', theme.mode);
    // Body background
    document.body.style.background = theme.mode === 'dark' ? '#0a0f1e' : '#f1f5f9';
    document.body.style.color = theme.mode === 'dark' ? '#e2e8f0' : '#0a0a0a';
  }, [theme]);

  const updateTheme = useCallback((partial: Partial<ThemeCfg>) => {
    setTheme(prev => ({ ...prev, ...partial }));
  }, []);

  const resetTheme = useCallback(() => setTheme(DEFAULT), []);

  const toggleMode = useCallback(() => {
    setTheme(prev => ({ ...prev, mode: prev.mode === 'dark' ? 'light' : 'dark' }));
  }, []);

  const isDark = theme.mode === 'dark';

  return (
    <ThemeCtx.Provider value={{ theme, updateTheme, resetTheme, toggleMode, isDark }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() { return useContext(ThemeCtx); }
